import type { ButtonHTMLAttributes, ReactElement, Ref } from 'react'
import { cn } from '@/lib/cn'

export type IconButtonVariant = 'ghost' | 'secondary' | 'danger'

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: becomes the accessible name, since the button renders an icon only. */
  label: string
  variant?: IconButtonVariant
  size?: 'sm' | 'md'
  ref?: Ref<HTMLButtonElement>
}

const variants: Record<IconButtonVariant, string> = {
  ghost: 'text-fg-muted hover:bg-sunken hover:text-fg',
  secondary: 'border border-line-strong bg-surface text-fg shadow-sheet hover:bg-sunken',
  danger: 'text-bad-600 hover:bg-bad-100 dark:text-bad-300 dark:hover:bg-bad-950'
}

const sizes: Record<NonNullable<IconButtonProps['size']>, string> = {
  sm: 'size-8 [&_svg]:size-4',
  md: 'size-10 [&_svg]:size-[1.125rem]'
}

export function IconButton({
  label,
  variant = 'ghost',
  size = 'md',
  className,
  children,
  ...rest
}: IconButtonProps): ReactElement {
  return (
    <button
      aria-label={label}
      className={cn(
        'inline-grid shrink-0 place-items-center rounded-control transition-colors duration-150 ease-out-quart',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        variants[variant],
        sizes[size],
        className
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
